import React, { useMemo, useState } from 'react';
import { Drawer, Tooltip } from 'antd';
import { FiCheck, FiX } from 'react-icons/fi';
import { Button } from '../../design-system';
import { CorrelationOptions } from '../../design-system/SamplesSidebar';
import { CalculationStrategies } from '../../@types/projects/types';
// Only offer samples with a real computed result per family (not merely the
// `performed_analyses` flag, which the backend also sets for skipped
// calculations). Same policy the Auto Correlation scope uses.
import { hasResultForStrategy } from '../../pages/MultiAnalysisPage/analysisCategory';
import { useCorrelation } from '../../providers/Correlation';
import { useAppContext } from '../../providers/AppContext';
import { useTheme } from '../../providers/theme';
import styles from './SamplesSummary.module.css';

type IconProps = { size?: number };
const CheckIcon = FiCheck as React.FC<IconProps>;
const CloseIcon = FiX as React.FC<IconProps>;

interface FamilyChip {
  strategy: CalculationStrategies;
  label: string;
  color: string;
}

const FAMILY_CHIPS: FamilyChip[] = [
  { strategy: CalculationStrategies.LIAO, label: 'Liao', color: '#7e57c2' },
  {
    strategy: CalculationStrategies.LAMBDA,
    label: 'Brooks-Corey',
    color: '#26a69a'
  },
  {
    strategy: CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY,
    label: 'Water-Oil',
    color: '#2196f3'
  },
  {
    strategy: CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY,
    label: 'Gas-Oil',
    color: '#f44336'
  }
];

interface SamplesSummaryProps {
  /**
   * Chart-scope control (X-axis toggle) — rendered as the FIRST item in the
   * bar, before the sample chips, so it leads the line.
   */
  axisControl?: React.ReactNode;
  /** Page actions (Rock Type Settings, Help) — anchored to the right end. */
  actions?: React.ReactNode;
  /**
   * Inline mode: renders only the chips + Edit button without the bar
   * chrome (border/sticky/padding), for embedding in the tab band row.
   */
  inline?: boolean;
}

/**
 * Selection summary bar + on-demand drawer for the Manual Correlation tab.
 * Replaces the persistent samples sidebar: one `selected/available` chip per
 * analysis family (live — chart point clicks update them through the
 * provider), with the full family → sample checkbox tree in a right-side
 * drawer. Edits keep the existing staged semantics: toggles stage locally in
 * the provider (worker preview), Save commits via the provider's confirm
 * flow, Cancel/close discards staged edits.
 */
const SamplesSummary = ({
  axisControl,
  actions,
  inline = false
}: SamplesSummaryProps) => {
  const { isDay } = useTheme();
  const { configStates } = useAppContext();
  const {
    projectSamples,
    handleSampleToggle,
    handleSelectAllStrategySamples,
    handleSaveCorrelationSamples,
    onCancelEditCorrelationSamples
  } = useCorrelation();

  const [drawerOpen, setDrawerOpen] = useState(false);

  const counts = useMemo(
    () =>
      FAMILY_CHIPS.map(chip => {
        const available = projectSamples.filter(sample =>
          hasResultForStrategy(sample, chip.strategy)
        );
        const selected = available.filter(
          sample => sample.selectedAnalyses?.[chip.strategy]
        );
        return {
          ...chip,
          selected: selected.length,
          available: available.length
        };
      }),
    [projectSamples]
  );

  const isEditing = Boolean(configStates.editCorrelationSamples);
  const isSaving = Boolean(configStates.loadingProjectSamples);

  /** Closing without saving discards staged edits (same as the sidebar cancel). */
  const discardAndClose = async () => {
    setDrawerOpen(false);
    if (isEditing) {
      await onCancelEditCorrelationSamples();
    }
  };

  const saveAndClose = async () => {
    await handleSaveCorrelationSamples();
    setDrawerOpen(false);
  };

  /** Commit the staged selection (chart clicks or drawer edits) without
   *  needing the drawer — recalculates the correlation on the backend. */
  const calculateFromStaged = async () => {
    await handleSaveCorrelationSamples();
  };

  return (
    <div
      className={inline ? styles.inline : styles.bar}
      data-testid="samples-summary-bar"
    >
      {axisControl ? (
        <div className={styles.axisControl}>{axisControl}</div>
      ) : null}
      <div className={styles.selection}>
        <div className={styles.chips}>
          {counts.map(chip => (
            <button
              key={chip.strategy}
              type="button"
              className={styles.chip}
              onClick={() => setDrawerOpen(true)}
              data-testid={`samples-chip-${chip.strategy}`}
              title={`${chip.label}: ${chip.selected} of ${chip.available} samples in the correlation — click to edit`}
            >
              <span
                className={styles.chipDot}
                style={{ backgroundColor: chip.color }}
              />
              {chip.label}{' '}
              <span className={styles.chipCount}>
                {chip.selected}/{chip.available}
              </span>
            </button>
          ))}
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setDrawerOpen(true)}
          data-testid="edit-samples-button"
        >
          Edit samples
        </Button>

        {isEditing ? (
          <div className={styles.pendingGroup} data-testid="samples-pending">
            <Tooltip title="You have unsaved sample changes">
              <span
                className={styles.pendingDot}
                aria-label="Unsaved sample changes"
              />
            </Tooltip>
            <Tooltip title="Calculate the correlation with the selected samples">
              <Button
                variant="primary"
                size="sm"
                className={styles.iconButton}
                onClick={calculateFromStaged}
                disabled={isSaving}
                loading={isSaving}
                aria-label="Calculate correlation with the selected samples"
                data-testid="calculate-correlation-button"
              >
                {!isSaving ? <CheckIcon size={15} /> : null}
              </Button>
            </Tooltip>
            <Tooltip title="Discard the sample changes">
              <Button
                variant="ghost"
                size="sm"
                className={styles.iconButton}
                onClick={onCancelEditCorrelationSamples}
                disabled={isSaving}
                aria-label="Discard the sample changes"
                data-testid="discard-selection-button"
              >
                <CloseIcon size={15} />
              </Button>
            </Tooltip>
          </div>
        ) : null}
      </div>

      {actions ? <div className={styles.actions}>{actions}</div> : null}

      <Drawer
        title="Correlation samples"
        placement="right"
        width={380}
        open={drawerOpen}
        onClose={discardAndClose}
        destroyOnClose
        footer={
          <div className={styles.drawerFooter}>
            <Button
              variant="secondary"
              onClick={discardAndClose}
              disabled={isSaving}
              data-testid="samples-drawer-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={saveAndClose}
              disabled={!isEditing || isSaving}
              loading={isSaving}
              data-testid="samples-drawer-save"
            >
              Save selection
            </Button>
          </div>
        }
      >
        <CorrelationOptions
          isDay={isDay}
          loadingProjectSamples={Boolean(configStates.loadingProjectSamples)}
          editCorrelationSamples={isEditing}
          projectSamples={projectSamples}
          handleSampleToggle={handleSampleToggle}
          handleSelectAllStrategySamples={handleSelectAllStrategySamples}
          isSampleAvailableForStrategy={hasResultForStrategy}
        />
      </Drawer>
    </div>
  );
};

export default SamplesSummary;
